/**
 * Missing Translations Modal Component
 * Displays a list of translation keys that are missing for a specific language
 */

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Badge } from '@/core/components/ui/badge';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { Skeleton } from '@/core/components/ui/skeleton';
import { ScrollArea } from '@/core/components/ui/scroll-area';
import { Search, Copy, Check, AlertCircle } from 'lucide-react';
import { copyToClipboard } from '@/core/utils/clipboard';
import { getMissingKeySummary } from '../../utils/missing-keys-utils';
import { parseCompoundLanguageCode } from '../../utils/language-code';
import { toast } from 'sonner';

interface MissingTranslationsModalProps {
  isOpen: boolean;
  languageCode: string | null;
  languageName: string;
  missingKeys: string[];
  isLoading: boolean;
  totalKeys: number;
  onClose: () => void;
}

export function MissingTranslationsModal({
  isOpen,
  languageCode,
  languageName,
  missingKeys,
  isLoading,
  totalKeys,
  onClose,
}: MissingTranslationsModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  // Filter keys based on search query
  const filteredKeys = useMemo(() => {
    if (!searchQuery.trim()) {
      return missingKeys;
    }
    const query = searchQuery.toLowerCase();
    return missingKeys.filter((key) => key.toLowerCase().includes(query));
  }, [missingKeys, searchQuery]);

  // Handle copying a single key
  const handleCopyKey = async (key: string) => {
    const success = await copyToClipboard(key);
    if (success) {
      setCopiedKey(key);
      toast.success('Key copied to clipboard');
      setTimeout(() => setCopiedKey(null), 2000);
    } else {
      toast.error('Failed to copy key');
    }
  };

  // Handle copying all keys
  const handleCopyAll = async () => {
    const allKeys = missingKeys.join('\n');
    const success = await copyToClipboard(allKeys);
    if (success) {
      setCopiedAll(true);
      toast.success(`Copied ${missingKeys.length} keys to clipboard`);
      setTimeout(() => setCopiedAll(false), 2000);
    } else {
      toast.error('Failed to copy keys');
    }
  };

  // Reset search when modal closes
  const handleClose = () => {
    setSearchQuery('');
    setCopiedKey(null);
    setCopiedAll(false);
    onClose();
  };

  // Generate summary text
  const summary = getMissingKeySummary(missingKeys.length, totalKeys);
  const operatorName = languageCode ? parseCompoundLanguageCode(languageCode).operatorName : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>
              {languageName}
              {operatorName && <span className="text-muted-foreground"> ({operatorName})</span>}
              {' - Missing Translation Keys'}
            </span>
            {!isLoading && missingKeys.length > 0 && (
              <Badge variant="secondary">{missingKeys.length}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {!isLoading && summary}
          </DialogDescription>
        </DialogHeader>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-3 py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {/* Error State - No keys loaded */}
        {!isLoading && !languageCode && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load missing keys. Please try again.
            </AlertDescription>
          </Alert>
        )}

        {/* Empty State - All translations complete */}
        {!isLoading && missingKeys.length === 0 && languageCode && (
          <div className="py-8 text-center">
            <div className="flex flex-col items-center gap-3">
              <Check className="h-12 w-12 text-green-600" />
              <div>
                <p className="font-medium text-green-600">All translations complete!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  This language has translations for all {totalKeys} keys.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content State - Show missing keys */}
        {!isLoading && missingKeys.length > 0 && (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search keys..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Copy All Button */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                {filteredKeys.length === missingKeys.length
                  ? `${filteredKeys.length} keys`
                  : `${filteredKeys.length} of ${missingKeys.length} keys`}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyAll}
                disabled={missingKeys.length === 0}
              >
                {copiedAll ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy All Keys
                  </>
                )}
              </Button>
            </div>

            {/* Keys List */}
            <ScrollArea className="flex-1 border rounded-md">
              {filteredKeys.length > 0 ? (
                <div className="p-4 space-y-2">
                  {filteredKeys.map((key) => (
                    <div
                      key={key}
                      className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-muted/50 group"
                    >
                      <code className="text-sm flex-1 font-mono break-all">
                        {key}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyKey(key)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      >
                        {copiedKey === key ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  No keys match your search "{searchQuery}"
                </div>
              )}
            </ScrollArea>
          </div>
        )}

        {/* Footer with Close Button */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
